import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate, useLocation } from 'react-router-dom';
import { CheckCircle, Loader2, Phone, Calendar, BarChart3, ArrowRight, XCircle, MessageSquare } from 'lucide-react';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SubscriptionSuccess = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [status, setStatus] = useState('checking'); // checking, success, failed
  const [paymentData, setPaymentData] = useState(null);
  const [biz, setBiz] = useState(null);

  useEffect(() => {
    const bid = paymentData?.metadata?.business_id;
    if (!bid) return;
    axios.get(`${API}/businesses/${bid}`).then((r) => setBiz(r.data)).catch(() => {});
  }, [paymentData]);

  const aiNumber = biz?.ai_phone_number;
  const phoneStep = aiNumber
    ? { desc: `Your AI answers at ${aiNumber}`, status: 'Ready' }
    : biz?.phone_setup_choice === 'forwarding'
      ? { desc: 'Your AI line is being set up. Our team will confirm your forwarding number shortly.', status: 'Setting Up' }
      : { desc: "We're assigning your dedicated number. Our team will email you if it takes longer than a few minutes.", status: 'Setting Up' };
  const sessionId = searchParams.get('session_id');
  const businessId = searchParams.get('business_id');
  
  // Detect what type of purchase this is
  const isSmsAddon = location.pathname.includes('sms-addon');
  const isMinutes = location.pathname.includes('minutes');

  useEffect(() => {
    if (sessionId) {
      pollPaymentStatus();
    } else {
      setStatus('failed');
    }
  }, [sessionId]);

  const pollPaymentStatus = async (attempts = 0) => {
    const maxAttempts = 10;
    const pollInterval = 2000;

    if (attempts >= maxAttempts) {
      setStatus('failed');
      return;
    }

    try {
      const res = await axios.get(`${API}/subscription/status/${sessionId}`);
      setPaymentData(res.data);

      if (res.data.payment_status === 'paid') {
        setStatus('success');
        return;
      } else if (res.data.status === 'expired') {
        setStatus('failed');
        return;
      }

      // Continue polling
      setTimeout(() => pollPaymentStatus(attempts + 1), pollInterval);
    } catch (err) {
      console.error('Error checking payment status:', err);
      
      // Check if it's a 404 (session not found) - likely payment was successful but session expired
      if (err.response?.status === 404) {
        // If Stripe says payment went through but we can't verify, assume success
        // User can verify in their dashboard
        setStatus('success');
        setPaymentData({
          payment_status: 'paid',
          metadata: { note: 'Payment verified via Stripe. Please check your dashboard for details.' }
        });
        return;
      }
      
      if (attempts < maxAttempts - 1) {
        setTimeout(() => pollPaymentStatus(attempts + 1), pollInterval);
      } else {
        setStatus('failed');
      }
    }
  };

  if (status === 'checking') {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <Card className="card-modern p-8 max-w-md w-full text-center">
          <Loader2 className="w-16 h-16 text-primary mx-auto mb-6 animate-spin" />
          <h1 className="text-2xl font-bold text-foreground mb-2">Processing Payment</h1>
          <p className="text-muted-foreground">Please wait while we confirm your subscription...</p>
        </Card>
      </div>
    );
  }

  if (status === 'failed') {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <Card className="card-modern p-8 max-w-md w-full text-center">
          <XCircle className="w-16 h-16 text-red-500 mx-auto mb-6" />
          <h1 className="text-2xl font-bold text-foreground mb-2">Payment Failed</h1>
          <p className="text-muted-foreground mb-6">
            There was an issue processing your payment. Please try again.
          </p>
          <Button onClick={() => navigate('/pricing')} className="bg-primary hover:bg-primary/90">
            Try Again
          </Button>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background py-12 px-4">
      <div className="max-w-2xl mx-auto">
        {/* Success Header */}
        <div className="text-center mb-8">
          <div className="w-20 h-20 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
            <CheckCircle className="w-12 h-12 text-green-500" />
          </div>
          {isSmsAddon ? (
            <>
              <h1 className="text-3xl font-bold text-foreground mb-2">Texting Add-on Purchased!</h1>
              <p className="text-muted-foreground text-lg">
                We're getting your texting number and submitting it for carrier approval. Texting turns on automatically once approved (usually 3-5 business days, up to 2 weeks).
              </p>
            </>
          ) : isMinutes ? (
            <>
              <h1 className="text-3xl font-bold text-foreground mb-2">Minutes Added!</h1>
              <p className="text-muted-foreground text-lg">
                Your account has been credited with additional minutes.
              </p>
            </>
          ) : (
            <>
              <h1 className="text-3xl font-bold text-foreground mb-2">Welcome to Front Desk Pro!</h1>
              <p className="text-muted-foreground text-lg">
                Your subscription is now active. Let's get your AI receptionist set up.
              </p>
            </>
          )}
        </div>

        {/* Payment Details */}
        {paymentData && (
          <Card className="card-modern p-6 mb-6">
            <h2 className="font-semibold text-foreground mb-4">Payment Confirmed</h2>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-muted-foreground">
                  {isSmsAddon ? 'Feature' : isMinutes ? 'Minutes' : 'Plan'}
                </span>
                <span className="text-foreground capitalize">
                  {isSmsAddon ? 'SMS Text Messaging' : isMinutes ? `${paymentData.metadata?.minutes || ''} Minutes` : ({ pro: 'Pro', starter: 'Starter' }[paymentData.metadata?.plan_id || paymentData.plan_id] || paymentData.metadata?.plan_id || '')}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Amount</span>
                <span className="text-foreground">${(paymentData.amount_total / 100).toFixed(2)} {paymentData.currency?.toUpperCase()}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Business</span>
                <span className="text-foreground">{paymentData.metadata?.business_name}</span>
              </div>
            </div>
          </Card>
        )}

        {/* Next Steps - different for SMS addon */}
        {isSmsAddon ? (
          <Card className="card-modern p-6 mb-6">
            <h2 className="font-semibold text-foreground mb-4">What's Next?</h2>
            <div className="space-y-4">
              {[
                { icon: MessageSquare, title: "Carrier Approval", desc: "Carriers usually approve within 3-5 business days (up to 2 weeks). Until then, new appointment alerts come to you by email. We'll email you when texting is live.", status: biz?.sms_feature_enabled ? 'Ready' : 'Pending Approval' },
                { icon: Phone, title: "Configure Reminders", desc: "Set up appointment reminders and notifications", status: "Ready" },
              ].map((step, i) => (
                <div key={i} className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
                    <step.icon className="w-5 h-5 text-primary" />
                  </div>
                  <div className="flex-1">
                    <h3 className="font-medium text-foreground">{step.title}</h3>
                    <p className="text-sm text-muted-foreground">{step.desc}</p>
                  </div>
                  <span className={`text-xs px-2 py-1 rounded-full ${
                    step.status === 'Ready' ? 'bg-green-500/15 text-green-700' : 'bg-amber-500/15 text-amber-700'
                  }`}>
                    {step.status}
                  </span>
                </div>
              ))}
            </div>
          </Card>
        ) : !isMinutes && (
          <Card className="card-modern p-6 mb-6">
            <h2 className="font-semibold text-foreground mb-4">What's Next?</h2>
            <div className="space-y-4">
              {[
                { icon: Phone, title: "Your Phone Number", desc: phoneStep.desc, status: phoneStep.status },
                { icon: Calendar, title: "Set Up Calendar", desc: "Configure your availability and booking rules", status: "Ready" },
                { icon: BarChart3, title: "Customize AI", desc: "Train your AI receptionist with your business details", status: "Ready" }
              ].map((step, i) => (
                <div key={i} className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
                    <step.icon className="w-5 h-5 text-primary" />
                  </div>
                  <div className="flex-1">
                    <h3 className="font-medium text-foreground">{step.title}</h3>
                    <p className="text-sm text-muted-foreground">{step.desc}</p>
                  </div>
                  <span className={`text-xs px-2 py-1 rounded-full ${
                    step.status === 'Ready' ? 'bg-green-500/15 text-green-700' : 'bg-amber-500/15 text-amber-700'
                  }`}>
                    {step.status}
                  </span>
                </div>
              ))}
            </div>
          </Card>
        )}

        {/* CTA */}
        <div className="flex flex-col sm:flex-row gap-4 justify-center">
          <Button 
            data-testid="go-to-dashboard"
            onClick={() => navigate(businessId ? `/business/${businessId}` : '/dashboard')} 
            className="bg-primary hover:bg-primary/90"
          >
            Go to Dashboard <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
          {!isSmsAddon && !isMinutes && (
            <Button variant="outline" onClick={() => navigate('/agent-settings')}>
              Customize AI
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

export default SubscriptionSuccess;
